// Port of the legacy `leg_setarrivaldate` (js/cms.js:5527) side effect that
// fires after a driver is assigned to a leg.
//
// Rule (unchanged from v1): when the order's dispatcher is not one of the
// pass-through carrier categories, the driver is NOT an in-house GWI driver,
// and the leg's arrival datetime is either blank or on a different day than the
// destination appointment date, set the arrival datetime:
//   - appt date == today  -> now, in browser-local time
//   - otherwise            -> the appt date at 00:00 local
//
// v1 gated this on `gwiDriver[driver] != 1`. There is no in-house/outside flag
// on our driver options, so we reproduce v1's data by the unit-id convention it
// encodes: a real GWI unit is purely numeric, the dummy outside carrier is
// "000", and other outside carriers are alpha codes. So "not a GWI driver"
// means "000" or any non-numeric value.

import { parseMdYDate, parseMdYHm, formatMdYHm } from './dateUtils';

// Dispatcher usernames that are carrier categories, not real GWI dispatch —
// v1 skips the arrival auto-set for these (js/cms.js:5541).
const PASS_THROUGH_DISPATCHERS = new Set(['highway', 'dallasdomestic', 'brokerage']);

/** A real in-house GWI driver has a purely-numeric unit id that isn't the
 * "000" dummy. Everything else (blank, "000", alpha outside-carrier codes) is
 * treated as non-GWI — matching v1's `gwiDriver` map. */
export function isGwiDriver(driver: string | null | undefined): boolean {
  const d = (driver ?? '').trim();
  return d !== '' && d !== '000' && /^\d+$/.test(d);
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The date/time to stamp on an outside-carrier (000) leg actual: the PRO's
 * appointment date for that end of the leg, with the CURRENT clock time of
 * day. The date is the PRO's (stamping "now" put the wrong day on a move
 * that outgated earlier); the time is real because EDI wants an actual time,
 * never 00:00 (Lee, 2026-10-29). An unparseable/blank appt date has nothing
 * to copy, so it falls back to now (the leg must still read complete the
 * moment 000 goes on). Format: MM/DD/YYYY HH:MM.
 */
export function outsideCarrierDateStamp(
  apptDate: string | null | undefined,
  now: Date = new Date(),
): string {
  const d = parseMdYDate(apptDate) ?? now;
  return formatMdYHm(
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), now.getHours(), now.getMinutes()),
  );
}

export interface DriverArrivalParams {
  /** order.dispatcher */
  dispatcher: string | null | undefined;
  /** the driver just assigned to the leg */
  driver: string | null | undefined;
  /** the leg's effective destination appointment date (MM/DD, MM/DD/YY, or MM/DD/YYYY) */
  destApptDate: string | null | undefined;
  /** the leg's effective destination arrival datetime (MM/DD/YYYY HH:MM) */
  destArrivalDateTime: string | null | undefined;
  /** injectable clock for tests; defaults to now */
  now?: Date;
}

/**
 * Returns the new `destArrivalDateTime` string to write, or `null` when no
 * change should be made (guards not met, or arrival already matches the appt
 * date). Format matches how the backend emits arrival datetimes: MM/DD/YYYY HH:MM.
 */
export function computeDriverArrivalDateTime(params: DriverArrivalParams): string | null {
  const dispatcher = (params.dispatcher ?? '').trim().toLowerCase();
  if (PASS_THROUGH_DISPATCHERS.has(dispatcher)) return null;

  // Only for non-GWI drivers (000 / outside carriers).
  if (isGwiDriver(params.driver)) return null;
  if (!(params.driver ?? '').trim()) return null;

  const apptDate = parseMdYDate(params.destApptDate);
  if (!apptDate) return null;

  // Skip if the arrival already lands on the appt date (v1's substring compare).
  const currentArrival = (params.destArrivalDateTime ?? '').trim();
  if (currentArrival) {
    const arrivalDate = parseMdYHm(currentArrival) ?? parseMdYDate(currentArrival.split(/\s+/)[0]);
    if (arrivalDate && sameCalendarDay(arrivalDate, apptDate)) return null;
  }

  const now = params.now ?? new Date();
  // appt date is today -> stamp "now" in local time; else the appt date at 00:00.
  return sameCalendarDay(apptDate, now) ? formatMdYHm(now) : formatMdYHm(apptDate);
}
